import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';

import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { assertExists } from 'common';
import { NGXLogger } from 'ngx-logger';
import { LiveFieldService } from 'src/app/services/live-field.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { FieldsQuery } from 'src/app/state/fields/fields.query';
import { FieldBaseComponent } from '../field-component.base.component';

@Component({
  selector: 'app-character-field',
  templateUrl: './character-field.component.html',
  styleUrls: ['./character-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CharacterFieldComponent
  extends FieldBaseComponent
  implements OnInit
{
  /** Whether to allow character fields above 150 characters to display as textarea. Defaults to false. */
  @Input()
  allowTextArea = false;
  /**
   * Whether the component is being displayed in a grid.
   *
   * This will adjust the number of rows show in the textarea and prevent resizing if set.
   */
  displayedInGrid = input(false);
  /** Input element reference. */
  @ViewChild('input')
  inputElement: ElementRef;
  textAreaAutoSize = viewChild('textAreaAutoSize', {
    read: CdkTextareaAutosize,
  });

  /** Determines how many rows are displayed in the textarea by default. */
  defaultDisplayedRows = computed(() => (this.displayedInGrid() ? 1 : 2));
  /** Determines whether the textarea will be automatically resized based on its contents. */
  enableTextAreaAutoResize = computed(
    () =>
      this.autoSizeTextAreaSettingEnabled() &&
      !this.displayedInGrid() &&
      this.allowTextAreaAutoResize()
  );

  private liveFieldLoadedInitially = false;
  /**
   * Whether to allow the textarea to be automatically resize.
   *
   * This is intended to allow us to enable and disable the behavior on focus and blur events.
   * it does not determine if the functionality will actually be enabled. That is controlled by {@link enableTextAreaAutoResize}.
   */
  private allowTextAreaAutoResize = signal(false);
  private autoSizeTextAreaSettingEnabled = toSignal(
    this.applicationQuery.autoSizeTextAreasToContentOnFocus$,
    { initialValue: false }
  );

  constructor(
    protected translate: TranslocoService,
    private applicationQuery: ApplicationQuery,
    private liveFieldService: LiveFieldService,
    private fieldsQuery: FieldsQuery,
    private logger: NGXLogger
  ) {
    super(translate);
  }

  /**
   * Whether the field should be displayed as a textarea.
   *
   * @returns True if the field should be displayed as a textarea.
   */
  get showFieldAsTextarea(): boolean {
    return this.allowTextArea && this.field().length > 150;
  }

  /** @inheritdoc */
  focus(): void {
    this.inputElement.nativeElement.focus();
    this.inputElement.nativeElement.select();
  }

  ngOnInit(): void {
    this.addValidators();

    // Invoke LiveField.
    const field = this.field();
    if (field.liveField) {
      this.control().disable(); // Make readonly, and exclude from validation.
      this.reloadLiveField();
    }
  }

  /** @inheritdoc */
  onBlur(event: FocusEvent): void {
    if (this.shouldBlur(event)) {
      if (this.enableTextAreaAutoResize()) {
        this.textAreaAutoSize()?.reset(); // Reset textarea size when blurring if the auto resize is enabled. Otherwise this will be undefined which is fine here.
      }
      this.allowTextAreaAutoResize.set(false); // Always ensure that auto resize is disabled on blur to save space.
      this.fieldBlurred.emit(this.field());
    }
  }

  /** @inheritdoc */
  onFocus(): void {
    this.allowTextAreaAutoResize.set(true);
    this.fieldFocused.emit(this.field());
  }

  /**
   * Reloads the live field value.
   */
  reloadLiveField(): void {
    const field = this.field();
    if (!this.liveFieldLoadedInitially) {
      const fieldFromStore = this.fieldsQuery.getField(field.id);
      assertExists(field.liveField);
      assertExists(fieldFromStore.liveField);
      field.liveField.url = fieldFromStore.liveField.url;
      field.liveField.body = fieldFromStore.liveField.body;
    } else {
      this.liveFieldLoadedInitially = true;
    }
    assertExists(field.liveField, 'Live field must be defined on the field.');
    field.liveField.setStatus('NOT_STARTED'); // Ensure the live field state is reset to the initial state.
    this.logger.debug('Loading LiveField:', field.liveField);
    this.liveFieldService.execute(field.liveField).subscribe((result) => {
      this.control().setValue(result);
      this.logger.debug('LiveField result:', result);
    });
  }
}
